import React, { useState, useRef, useEffect } from 'react';
import { View, StyleSheet, Platform, Text } from 'react-native';
import { XCircle } from './icons';

// Conditional import for react-dom
let createPortal: any;
if (Platform.OS === 'web') {
  try {
    // @ts-ignore
    createPortal = require('react-dom').createPortal;
  } catch (e) {
    console.warn('react-dom not available');
  }
}

interface FailedIndicatorProps {
  size?: 'small' | 'medium' | 'large';
  errorMessage?: string | null;
}

export const FailedIndicator: React.FC<FailedIndicatorProps> = ({
  size = 'small',
  errorMessage,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number; position: 'above' | 'below'; transformX: string } | null>(null);
  const indicatorRef = useRef<View>(null);

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          iconSize: 14,
        };
      case 'large':
        return {
          iconSize: 18,
        };
      default:
        return {
          iconSize: 16,
        };
    }
  };

  const sizeStyles = getSizeStyles();

  // Calculate tooltip position when hovering
  useEffect(() => {
    if (Platform.OS === 'web' && isHovered && errorMessage && indicatorRef.current) {
      const updateTooltipPosition = () => {
        if (indicatorRef.current) {
          // @ts-ignore - web-only method
          const rect = indicatorRef.current.getBoundingClientRect();
          if (rect) {
            const tooltipHeight = 60;
            const estimatedTooltipWidth = 250; // Estimated width for tooltip
            const spaceAbove = rect.top;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceLeft = rect.left;
            const spaceRight = window.innerWidth - rect.right;
            
            const shouldPositionBelow = spaceAbove < tooltipHeight && spaceBelow > tooltipHeight;
            const iconCenterX = rect.left + rect.width / 2;
            
            // Calculate horizontal positioning to avoid cutoff
            let tooltipLeft = iconCenterX;
            let transformX = '-50%'; // Default: center on icon
            
            // Check if tooltip would be cut off on the left
            if (iconCenterX - estimatedTooltipWidth / 2 < 10) {
              // Position tooltip to start near the left edge with some padding
              tooltipLeft = 10;
              transformX = '0%';
            }
            // Check if tooltip would be cut off on the right
            else if (iconCenterX + estimatedTooltipWidth / 2 > window.innerWidth - 10) {
              // Position tooltip to end near the right edge with some padding
              tooltipLeft = window.innerWidth - 10;
              transformX = '100%';
            }
            
            setTooltipPosition({
              top: shouldPositionBelow ? rect.bottom + 8 : rect.top - 8,
              left: tooltipLeft,
              position: shouldPositionBelow ? 'below' : 'above',
              transformX: transformX,
            });
          }
        }
      };

      updateTooltipPosition();
      window.addEventListener('scroll', updateTooltipPosition, true);
      window.addEventListener('resize', updateTooltipPosition);

      return () => {
        window.removeEventListener('scroll', updateTooltipPosition, true);
        window.removeEventListener('resize', updateTooltipPosition);
      };
    } else {
      setTooltipPosition(null);
    }
  }, [isHovered, errorMessage]);

  return (
    <>
      <View
        ref={indicatorRef}
        style={styles.container}
        {...Platform.select({
          web: {
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          },
        })}
      >
        <View style={styles.iconWrapper}>
          <XCircle 
            size={sizeStyles.iconSize} 
            color="#dc3545" 
            fill="none"
            strokeWidth={2.5}
          />
        </View>
      </View>
      {/* Portal tooltip - rendered at document body level */}
      {Platform.OS === 'web' && isHovered && errorMessage && tooltipPosition && createPortal && (
        createPortal(
          <div
            data-failed-indicator-tooltip="true"
            style={{
              position: 'fixed',
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              backgroundColor: '#333',
              padding: '8px',
              borderRadius: '6px',
              minWidth: '200px',
              maxWidth: '400px',
              zIndex: 1000000,
              transform: tooltipPosition.position === 'above' 
                ? `translate(${tooltipPosition.transformX}, -100%)` 
                : `translate(${tooltipPosition.transformX}, 0)`,
              marginBottom: tooltipPosition.position === 'above' ? '4px' : '0',
              marginTop: tooltipPosition.position === 'below' ? '4px' : '0',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
              pointerEvents: 'none',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div style={{
              fontSize: '12px',
              color: '#fff',
              lineHeight: '16px',
            }}>
              {errorMessage}
            </div>
          </div>,
          typeof document !== 'undefined' ? document.body : null as any
        )
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  },
});

